<template>
  <div class="doc-new">
    <!-- ─── Step 1: Selector de vía de creación ──────────────────────── -->
    <template v-if="step === 'select'">
      <TopBarTitleComponent title="Nuevo documento" :showBackIcon="true">
        <template slot="right-area">
          <div></div>
        </template>
      </TopBarTitleComponent>

      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <div class="doc-new__selector">
          <p class="doc-new__selector-title">
            ¿Cómo quieres crear tu documento?
          </p>
          <div class="doc-new__selector-grid">
            <!-- Usar plantilla -->
            <div class="doc-new__card" @click="step = 'template'">
              <div class="doc-new__card-icon">
                <i class="el-icon-document-copy" />
              </div>
              <p class="doc-new__card-title">Usar plantilla</p>
              <p class="doc-new__card-desc">
                Selecciona una plantilla predefinida y personalízala.
              </p>
            </div>
            <!-- Crear desde cero -->
            <div class="doc-new__card" @click="initScratch()">
              <div class="doc-new__card-icon doc-new__card-icon--accent">
                <i class="el-icon-edit-outline" />
              </div>
              <p class="doc-new__card-title">Crear desde cero</p>
              <p class="doc-new__card-desc">
                Escribe tu documento con el editor de texto enriquecido.
              </p>
            </div>
            <!-- Cargar PDF -->
            <div class="doc-new__card" @click="step = 'upload'">
              <div class="doc-new__card-icon doc-new__card-icon--green">
                <i class="el-icon-upload2" />
              </div>
              <p class="doc-new__card-title">Cargar PDF</p>
              <p class="doc-new__card-desc">
                Sube un PDF existente para agregarlo al flujo de firma.
              </p>
            </div>
          </div>
        </div>
      </b-container>
    </template>

    <!-- ─── Step 1b: Selector de plantilla ───────────────────────────── -->
    <template v-if="step === 'template'">
      <TopBarTitleComponent title="Seleccionar plantilla" :showBackIcon="true">
        <template slot="right-area">
          <div>
            <button
              class="btn md__btn-third-secondary"
              @click="step = 'select'"
            >
              Volver
            </button>
          </div>
        </template>
      </TopBarTitleComponent>

      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <div v-if="loadingTemplates" class="text-center py-5">
          <i class="el-icon-loading" style="font-size:24px" />
        </div>
        <div v-else-if="templates.length" class="doc-new__tpl-grid">
          <div
            class="doc-new__tpl-card"
            v-for="tpl in templates"
            :key="tpl.id"
            @click="selectTemplate(tpl)"
          >
            <div class="doc-new__tpl-icon">
              <i class="el-icon-document" />
            </div>
            <p class="doc-new__tpl-name">{{ tpl.name }}</p>
            <p class="doc-new__tpl-cat" v-if="tpl.category">
              {{ tpl.category }}
            </p>
          </div>
        </div>
        <div v-else class="text-center py-5">
          <p class="text-muted">No hay plantillas disponibles.</p>
          <button class="btn md__btn-primary mt-2" @click="initScratch()">
            Crear desde cero
          </button>
        </div>
      </b-container>
    </template>

    <!-- ─── Step 2: Upload PDF ───────────────────────────────────────── -->
    <template v-if="step === 'upload'">
      <TopBarTitleComponent title="Cargar PDF" :showBackIcon="true">
        <template slot="right-area">
          <div>
            <button
              class="btn md__btn-third-secondary"
              @click="step = 'select'"
            >
              Volver
            </button>
          </div>
        </template>
      </TopBarTitleComponent>

      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <div class="doc-new__upload-area">
          <el-form
            :model="uploadForm"
            :rules="uploadRules"
            ref="uploadForm"
            label-position="top"
          >
            <el-form-item label="Nombre del documento" prop="name">
              <el-input
                v-model="uploadForm.name"
                placeholder="Ej: Contrato de servicios"
              />
            </el-form-item>
            <el-form-item label="Tipo" prop="type">
              <el-select
                v-model="uploadForm.type"
                placeholder="Seleccionar tipo"
              >
                <el-option label="Contrato" value="contrato" />
                <el-option label="NDA" value="nda" />
                <el-option label="Propuesta" value="propuesta" />
                <el-option label="Alcance" value="alcance" />
                <el-option label="Otro" value="otro" />
              </el-select>
            </el-form-item>
            <el-form-item label="Archivo PDF" prop="file">
              <el-upload
                ref="pdfUpload"
                drag
                :auto-upload="false"
                :limit="1"
                accept=".pdf"
                :on-change="onFileChange"
                :on-remove="onFileRemove"
                :file-list="uploadFileList"
              >
                <i class="el-icon-upload" />
                <div class="el-upload__text">
                  Arrastra tu archivo aquí o <em>haz clic para seleccionar</em>
                </div>
                <div class="el-upload__tip" slot="tip">
                  Solo archivos PDF, máximo 10MB
                </div>
              </el-upload>
            </el-form-item>
            <div class="text-right mt-3">
              <button
                type="button"
                class="btn md__btn-primary"
                :disabled="savingUpload"
                @click="submitUpload"
              >
                <i class="el-icon-loading" v-if="savingUpload" />
                Subir y continuar
              </button>
            </div>
          </el-form>
        </div>
      </b-container>
    </template>

    <!-- ─── Step 3: Editor (plantilla o desde cero) ──────────────────── -->
    <template v-if="step === 'editor'">
      <div class="doc-editor-header">
        <div class="doc-editor-header__left">
          <button class="doc-editor-header__back" @click="confirmBack">
            <i class="el-icon-arrow-left" />
          </button>
          <input
            class="doc-editor-header__title-input"
            v-model="form.name"
            placeholder="Nombre del documento"
          />
        </div>
        <div class="doc-editor-header__actions">
          <button
            class="btn btn-sm btn-outline-secondary mr-2"
            @click="previewDocument"
          >
            <i class="el-icon-view" /> Vista previa
          </button>
          <button
            class="btn md__btn-primary"
            :disabled="saving"
            @click="saveDocument"
          >
            <i class="el-icon-loading" v-if="saving" />
            <i class="el-icon-check" v-else />
            Guardar
          </button>
        </div>
      </div>

      <div class="doc-editor-body">
        <!-- Columna izquierda: Editor TipTap -->
        <div class="doc-editor-body__editor">
          <!-- Toolbar -->
          <div class="doc-toolbar" v-if="editor">
            <button
              class="doc-toolbar__btn"
              :class="{ 'doc-toolbar__btn--active': editor.isActive('bold') }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleBold()
                  .run()
              "
              title="Negrita"
            >
              <b>B</b>
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{ 'doc-toolbar__btn--active': editor.isActive('italic') }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              "
              title="Cursiva"
            >
              <i>I</i>
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('underline')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleUnderline()
                  .run()
              "
              title="Subrayado"
            >
              <u>U</u>
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('heading', {
                  level: 1
                })
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 1 })
                  .run()
              "
              title="Título 1"
            >
              H1
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('heading', {
                  level: 2
                })
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 2 })
                  .run()
              "
              title="Título 2"
            >
              H2
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('bulletList')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleBulletList()
                  .run()
              "
              title="Lista"
            >
              <i class="el-icon-s-operation" />
            </button>
            <button
              class="doc-toolbar__btn"
              :class="{
                'doc-toolbar__btn--active': editor.isActive('orderedList')
              }"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleOrderedList()
                  .run()
              "
              title="Lista numerada"
            >
              <i class="el-icon-s-order" />
            </button>
            <span class="doc-toolbar__sep" />
            <button class="doc-toolbar__btn" @click="insertTable" title="Tabla">
              <i class="el-icon-s-grid" />
            </button>
            <button
              class="doc-toolbar__btn"
              @click="insertImage"
              title="Imagen"
            >
              <i class="el-icon-picture-outline" />
            </button>
            <span class="doc-toolbar__sep" />
            <button
              class="doc-toolbar__btn doc-toolbar__btn--signature"
              @click="insertSignatureZone"
              title="Zona de firma"
            >
              <i class="el-icon-edit" /> Firma
            </button>
          </div>

          <!-- TipTap editor content -->
          <div class="doc-editor-content">
            <editor-content :editor="editor" />
          </div>
        </div>

        <!-- Columna derecha: Detalles -->
        <div class="doc-editor-body__sidebar">
          <div class="doc-sidebar-section">
            <p class="doc-sidebar-section__title">Detalles</p>
            <el-form label-position="top" size="small">
              <el-form-item label="Tipo de documento">
                <el-select v-model="form.type" placeholder="Seleccionar">
                  <el-option label="Contrato" value="contrato" />
                  <el-option label="NDA" value="nda" />
                  <el-option label="Propuesta" value="propuesta" />
                  <el-option label="Alcance" value="alcance" />
                  <el-option label="Otro" value="otro" />
                </el-select>
              </el-form-item>
              <el-form-item label="Expiración (opcional)">
                <el-date-picker
                  v-model="form.expires_at"
                  type="date"
                  placeholder="Sin expiración"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                />
              </el-form-item>
              <el-form-item label="Recordatorio">
                <el-select
                  v-model="form.reminder_frequency"
                  placeholder="Sin recordatorio"
                >
                  <el-option label="Sin recordatorio" :value="null" />
                  <el-option label="Cada 2 días" value="2_days" />
                  <el-option label="Semanal" value="weekly" />
                </el-select>
              </el-form-item>
            </el-form>
          </div>

          <div
            class="doc-sidebar-section"
            v-if="form.source === 'template' && selectedTemplate"
          >
            <p class="doc-sidebar-section__title">Plantilla</p>
            <p class="doc-sidebar-section__text">{{ selectedTemplate.name }}</p>
          </div>

          <div class="doc-sidebar-section">
            <p class="doc-sidebar-section__title">Zonas de firma detectadas</p>
            <p class="doc-sidebar-section__text" v-if="!signatureZones.length">
              Usa el botón "Firma" en la barra para insertar zonas de firma en
              el documento.
            </p>
            <div v-else>
              <div
                class="doc-sidebar-sigzone"
                v-for="(zone, idx) in signatureZones"
                :key="idx"
              >
                <span>Firma {{ idx + 1 }}</span>
                <button
                  class="doc-sidebar-sigzone__remove"
                  @click="removeSignatureZone(idx)"
                >
                  <i class="el-icon-close" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal vista previa -->
      <el-dialog
        :visible.sync="previewVisible"
        fullscreen
        :show-close="false"
        class="doc-preview-dialog"
      >
        <div class="doc-preview-header">
          <p class="doc-preview-header__title">Vista previa</p>
          <button
            class="doc-preview-header__close"
            @click="previewVisible = false"
          >
            <i class="el-icon-close" />
          </button>
        </div>
        <div class="doc-preview-body" v-html="previewHtml" />
      </el-dialog>
    </template>
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import documentoService from "../services/documentoService";

import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Image } from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Placeholder } from "@tiptap/extension-placeholder";

export default {
  name: "DocumentNewPage",
  components: {
    TopBarTitleComponent,
    EditorContent
  },
  props: {
    query: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      step: "select",
      editor: null,
      saving: false,

      // Template selection
      templates: [],
      loadingTemplates: false,
      selectedTemplate: null,

      // Editor form
      form: {
        name: "",
        type: "contrato",
        source: "scratch",
        content: "",
        expires_at: null,
        reminder_frequency: null,
        user_business_document_template_id: null
      },

      signatureZones: [],

      // Upload form
      uploadForm: {
        name: "",
        type: "contrato",
        file: null
      },
      uploadRules: {
        name: [
          { required: true, message: "Ingresa el nombre", trigger: "blur" }
        ],
        type: [
          { required: true, message: "Selecciona el tipo", trigger: "change" }
        ]
      },
      uploadFileList: [],
      savingUpload: false,

      // Preview
      previewVisible: false,
      previewHtml: ""
    };
  },
  methods: {
    // ─── Template flow ──────────────────────────────────────────────
    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const response = await documentoService.getTemplates();
        const { success, data } = response.data;
        if (success) {
          this.templates = data || [];
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al cargar plantillas" });
      } finally {
        this.loadingTemplates = false;
      }
    },
    selectTemplate(tpl) {
      this.selectedTemplate = tpl;
      this.form.source = "template";
      this.form.name = tpl.name;
      this.form.user_business_document_template_id = tpl.id;
      this.form.content = tpl.content || "";
      if (tpl.signature_zones && tpl.signature_zones.length) {
        this.signatureZones = [...tpl.signature_zones];
      }
      this.initEditor(tpl.content || "");
      this.step = "editor";
    },

    // ─── Scratch flow ───────────────────────────────────────────────
    initScratch() {
      this.form.source = "scratch";
      this.form.name = "";
      this.form.content = "";
      this.initEditor("");
      this.step = "editor";
    },

    // ─── TipTap Editor ──────────────────────────────────────────────
    initEditor(content) {
      if (this.editor) {
        this.editor.destroy();
      }
      this.editor = new Editor({
        content: content || "",
        extensions: [
          StarterKit,
          Underline,
          Image,
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          Placeholder.configure({
            placeholder: "Escribe el contenido de tu documento aquí..."
          })
        ],
        onUpdate: ({ editor }) => {
          this.form.content = editor.getHTML();
        }
      });
    },

    insertTable() {
      this.editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },

    insertImage() {
      const url = prompt("URL de la imagen:");
      if (url) {
        this.editor
          .chain()
          .focus()
          .setImage({ src: url })
          .run();
      }
    },

    insertSignatureZone() {
      const idx = this.signatureZones.length + 1;
      this.signatureZones.push({
        label: `Firmante ${idx}`,
        x: 10,
        y: 80,
        width: 30,
        height: 8,
        page: 1
      });
      // Insert visual placeholder in editor
      this.editor
        .chain()
        .focus()
        .insertContent(
          `<p style="border:2px dashed #3ecfb4;padding:12px;border-radius:8px;text-align:center;color:#3ecfb4;font-weight:600;">` +
            `[Zona de firma ${idx}]</p>`
        )
        .run();
    },

    removeSignatureZone(idx) {
      this.signatureZones.splice(idx, 1);
    },

    // ─── Save document ──────────────────────────────────────────────
    async saveDocument() {
      if (!this.form.name) {
        this.$notifications.warning({
          message: "Ingresa un nombre para el documento"
        });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          type: this.form.type,
          source: this.form.source,
          content: this.form.content,
          expires_at: this.form.expires_at,
          reminder_frequency: this.form.reminder_frequency,
          signature_zones: this.signatureZones
        };
        if (this.form.user_business_document_template_id) {
          payload.user_business_document_template_id = this.form.user_business_document_template_id;
        }
        const response = await documentoService.createDocument(payload);
        const { success, data } = response.data;
        if (success) {
          this.$notifications.success({ message: "Documento creado" });
          this.$router.push({
            name: "documentos.detail",
            params: { id: data.id }
          });
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al crear"
          });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al guardar el documento" });
      } finally {
        this.saving = false;
      }
    },

    // ─── Upload PDF ─────────────────────────────────────────────────
    onFileChange(file) {
      if (file.raw && file.raw.size > 10 * 1024 * 1024) {
        this.$notifications.warning({
          message: "El archivo no puede superar 10MB"
        });
        this.uploadFileList = [];
        this.uploadForm.file = null;
        return;
      }
      this.uploadForm.file = file.raw;
    },
    onFileRemove() {
      this.uploadForm.file = null;
    },
    submitUpload() {
      this.$refs.uploadForm.validate(async valid => {
        if (!valid) return;
        if (!this.uploadForm.file) {
          this.$notifications.warning({ message: "Selecciona un archivo PDF" });
          return;
        }
        this.savingUpload = true;
        try {
          const response = await documentoService.uploadDocumentPdf({
            file: this.uploadForm.file,
            name: this.uploadForm.name,
            type: this.uploadForm.type
          });
          const { success } = response.data;
          if (success) {
            this.$notifications.success({ message: "Documento creado" });
            this.$router.push({ name: "documentos" });
          } else {
            this.$notifications.error({
              message: response.data.message || "Error al subir"
            });
          }
        } catch (e) {
          this.$notifications.error({ message: "Error al subir el archivo" });
        } finally {
          this.savingUpload = false;
        }
      });
    },

    // ─── Preview ────────────────────────────────────────────────────
    previewDocument() {
      this.previewHtml = this.form.content;
      this.previewVisible = true;
    },

    // ─── Navigation ─────────────────────────────────────────────────
    confirmBack() {
      if (this.form.content) {
        this.$confirm(
          "¿Salir sin guardar? Los cambios se perderán.",
          "Confirmar",
          {
            confirmButtonText: "Salir",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        )
          .then(() => {
            this.goBack();
          })
          .catch(() => {});
      } else {
        this.goBack();
      }
    },
    goBack() {
      if (this.editor) {
        this.editor.destroy();
        this.editor = null;
      }
      this.step = "select";
    }
  },
  mounted() {
    // Si viene con query.template, cargar directo esa plantilla
    const templateId = this.$route.query.template;
    if (templateId) {
      this.loadingTemplates = true;
      documentoService
        .getTemplate(templateId)
        .then(response => {
          const { success, data } = response.data;
          if (success && data) {
            this.selectTemplate(data);
          } else {
            this.step = "select";
          }
        })
        .catch(() => {
          this.step = "select";
        })
        .finally(() => {
          this.loadingTemplates = false;
        });
    }
    this.loadTemplates();
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  }
};
</script>

<style lang="scss" scoped>
/* ─── Selector de vía ────────────────────────────────────────────── */
.doc-new__selector {
  max-width: 800px;
  margin: 40px auto;
  text-align: center;
}
.doc-new__selector-title {
  font-size: 20px;
  font-weight: 600;
  color: #31355c;
  margin-bottom: 32px;
}
.doc-new__selector-grid {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}
.doc-new__card {
  background: #fff;
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 32px 24px;
  width: 220px;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
  text-align: center;
  box-shadow: 0 0 10px 0 #d6e4f3;
  &:hover {
    border-color: #3fdbbf;
    transform: translateY(-4px);
    box-shadow: 0 6px 24px rgba(63, 219, 191, 0.15);
  }
}
.doc-new__card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e4eaf6;
  color: #31355c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto 14px;
  &--accent {
    background: #e6faf5;
    color: #1d9e75;
  }
  &--green {
    background: #e1f5ee;
    color: #0f6e56;
  }
}
.doc-new__card-title {
  font-size: 14px;
  font-weight: 600;
  color: #31355c;
  margin: 0 0 6px;
}
.doc-new__card-desc {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
}

/* ─── Template grid ──────────────────────────────────────────────── */
.doc-new__tpl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}
.doc-new__tpl-card {
  background: #fff;
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
  box-shadow: 0 0 10px 0 #d6e4f3;
  &:hover {
    border-color: #3fdbbf;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(214, 228, 243, 0.6);
  }
}
.doc-new__tpl-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #faeeda;
  color: #854f0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-bottom: 10px;
}
.doc-new__tpl-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
}
.doc-new__tpl-cat {
  font-size: 11px;
  color: #9ca3af;
  margin: 4px 0 0;
  text-transform: capitalize;
}

/* ─── Upload ─────────────────────────────────────────────────────── */
.doc-new__upload-area {
  max-width: 500px;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid #e4eaf6;
  padding: 32px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}

/* ─── Editor header ──────────────────────────────────────────────── */
.doc-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #e8e7e3;
  position: sticky;
  top: 0;
  z-index: 10;
}
.doc-editor-header__left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.doc-editor-header__back {
  border: none;
  background: none;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  color: #6b7280;
  margin-right: 8px;
  &:hover {
    background: #f3f4f6;
  }
}
.doc-editor-header__title-input {
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 600;
  color: #31355c;
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 6px;
  &:focus {
    background: #f9fafb;
  }
  &::placeholder {
    color: #d1d5db;
  }
}
.doc-editor-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* ─── Editor body ────────────────────────────────────────────────── */
.doc-editor-body {
  display: flex;
  height: calc(100vh - 56px);
  overflow: hidden;
}
.doc-editor-body__editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #e8e7e3;
}
.doc-editor-body__sidebar {
  width: 280px;
  overflow-y: auto;
  padding: 20px;
  flex-shrink: 0;
  background: #fafafa;
}

/* ─── Toolbar ────────────────────────────────────────────────────── */
.doc-toolbar {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #e8e7e3;
  background: #fff;
  flex-wrap: wrap;
  gap: 2px;
}
.doc-toolbar__btn {
  border: none;
  background: none;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #6b7280;
  transition: all 0.15s;
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
  &--active {
    background: #e4eaf6;
    color: #31355c;
    font-weight: 600;
  }
  &--signature {
    color: #3fdbbf;
    font-weight: 500;
    &:hover {
      background: #e6faf5;
    }
  }
}
.doc-toolbar__sep {
  width: 1px;
  height: 20px;
  background: #e8e7e3;
  margin: 0 4px;
}

/* ─── Editor content ─────────────────────────────────────────────── */
.doc-editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  background: #f9fafb;
}
.doc-editor-content ::v-deep .ProseMirror {
  background: #fff;
  border: 1px solid #e8e7e3;
  border-radius: 8px;
  padding: 40px;
  min-height: 600px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  outline: none;
  font-size: 14px;
  line-height: 1.7;
  color: #1f2937;

  h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 24px 0 12px;
  }
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 20px 0 10px;
  }
  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 16px 0 8px;
  }

  p {
    margin: 0 0 12px;
  }

  ul,
  ol {
    padding-left: 24px;
    margin: 0 0 12px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    td,
    th {
      border: 1px solid #d1d5db;
      padding: 8px 12px;
      font-size: 13px;
    }
    th {
      background: #f3f4f6;
      font-weight: 600;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #d1d5db;
    pointer-events: none;
    height: 0;
  }
}

/* ─── Sidebar ────────────────────────────────────────────────────── */
.doc-sidebar-section {
  margin-bottom: 24px;
  &__title {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 10px;
  }
  &__text {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
}
.doc-sidebar-sigzone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e8e7e3;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  margin-bottom: 6px;
  &__remove {
    border: none;
    background: none;
    color: #e24b4a;
    cursor: pointer;
    padding: 2px;
    font-size: 14px;
  }
}

/* ─── Preview dialog ─────────────────────────────────────────────── */
.doc-preview-dialog ::v-deep .el-dialog {
  margin: 0 !important;
  border-radius: 0;
}
.doc-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #e8e7e3;
  background: #fff;
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
  &__close {
    border: none;
    background: none;
    font-size: 20px;
    cursor: pointer;
    color: #6b7280;
    padding: 4px 8px;
    border-radius: 6px;
    &:hover {
      background: #f3f4f6;
    }
  }
}
.doc-preview-body {
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.7;
  color: #1f2937;
}

/* ─── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .doc-new__selector-grid {
    flex-direction: column;
    align-items: center;
  }
  .doc-new__card {
    width: 100%;
    max-width: 300px;
  }
  .doc-editor-body {
    flex-direction: column;
    height: auto;
  }
  .doc-editor-body__sidebar {
    width: 100%;
    border-top: 1px solid #e8e7e3;
  }
  .doc-editor-body__editor {
    border-right: none;
  }
}
</style>
